import classNames from '@/common/utils/classNames';
import Switch from '@/common/components/atom/switch';
import Modal from '@/common/components/atom/modal';
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import toast from 'react-hot-toast';
import moment from 'jalali-moment';
import { createPortal } from 'react-dom';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  deleteBooksAfterDeactiveOnlineVisit,
  deleteBooksAfterDeactiveOnlineVisitGuarded,
  disableOnlineVisitGuarded,
  enableOnlineVisitGuarded,
  getOnlineVisitCountAfterDeactivation,
  toggleCanBooking,
  useOnlineVisitStatus,
  useSetOnlineVisitStatus,
  type OnlineVisitGuardChat,
} from '../apis/onlineVisit';
import { DsButton, DsCard, DsSectionHeader, ds } from '../designSystem';
import { DsDrawer } from './DsDrawer';
import { OnlineVisitGuardChats } from './onlineVisitGuardChats';
import { useDoctorHomeSheetHost, useSheetDrawerProps } from '../hooks/doctorHomeSheetLayout';
import { useSheetRoute } from '../hooks/useSheetRoute';
import { useSelectedCenterStore } from '../store/selectedCenter';
import { useSelectedDateStore } from '../store/selectedDate';
import { sendDoctorHomeEvent } from '../utils/analytics';
import { useOvBookingGuardEnabled } from '../utils/ovBookingGuardFlag';
import { DOCTOR_PANEL_URLS } from '../utils/doctorPanelUrls';
import { formatDoctorCenterAddressHint, formatDoctorCenterName, getType1ClinicCenters, hasOnlineVisitCenter, isType1Center, ONLINE_VISIT_CENTER_ID, shouldShowClinicSection, shouldShowOnlineVisitSection, getOnlineVisitCenter } from '../utils/centers';
import { buildDoctorPanelUrl } from '../utils/iframeUrl';
import { dsFocusRing } from '../utils/a11y';
import {
  BellIcon,
  BookingSettingsIcon,
  ChatIcon,
  ClinicIcon,
  ClockIcon,
  TariffIcon,
  VacationIcon,
} from './icons';
import { FeedStaggerItem } from './feedContentSwap';
import { RowChevron } from './feedWidgets/widgetListRow';

type ActionId = 'workhours' | 'vacation' | 'tariff' | 'bookingSettings' | 'notifications';

/** Temporarily hide appointment-SMS shortcut from doctor-home action sheets. */
const SHOW_NOTIFICATIONS_SHORTCUT = false;

const SUB_ACTIONS: {
  id: ActionId;
  label: string;
  hint: string;
  description: string;
  url: string;
  analyticsFeature:
    | 'shortcut_workhours'
    | 'shortcut_vacation'
    | 'shortcut_tariff'
    | 'shortcut_booking_settings'
    | 'shortcut_notifications';
  icon: typeof ClockIcon;
}[] = [
  {
    id: 'workhours',
    label: 'ساعات کاری',
    hint: 'زمان پذیرش',
    description: 'تنظیم ساعات کاری',
    url: DOCTOR_PANEL_URLS.workhours,
    analyticsFeature: 'shortcut_workhours',
    icon: ClockIcon,
  },
  {
    id: 'vacation',
    label: 'اعلام تعطیلی',
    hint: 'مرخصی مطب',
    description: 'مرخصی',
    url: DOCTOR_PANEL_URLS.vacation,
    analyticsFeature: 'shortcut_vacation',
    icon: VacationIcon,
  },
  {
    id: 'tariff',
    label: 'تعرفه‌ها',
    hint: 'قیمت خدمات',
    description: 'تغییر تعرفه‌ها',
    url: DOCTOR_PANEL_URLS.tariffs,
    analyticsFeature: 'shortcut_tariff',
    icon: TariffIcon,
  },
  {
    id: 'bookingSettings',
    label: 'تنظیمات نوبت‌دهی',
    hint: 'تنظیم محدودیت‌های ثبت نوبت و ...',
    description: 'تنظیمات نوبت‌دهی',
    url: DOCTOR_PANEL_URLS.bookingSettings,
    analyticsFeature: 'shortcut_booking_settings',
    icon: BookingSettingsIcon,
  },
  {
    id: 'notifications',
    label: 'اطلاع‌رسانی',
    hint: 'پیامک به بیمار، پزشک یا منشی',
    description: 'تنظیم پیامک‌های خودکار نوبت',
    url: DOCTOR_PANEL_URLS.notifications,
    analyticsFeature: 'shortcut_notifications',
    icon: BellIcon,
  },
].filter(action => SHOW_NOTIFICATIONS_SHORTCUT || action.id !== 'notifications');

const ActionIcon = ({
  children,
  size = 'md',
}: {
  children: ReactNode;
  size?: 'md' | 'lg';
}) => (
  <span
    className={classNames(
      ds.icon.containerAction,
      ds.radius.inner,
      size === 'lg' ? 'h-10 w-10' : 'h-9 w-9',
    )}
  >
    {children}
  </span>
);

const PanelIframeDrawer = ({
  open,
  onClose,
  title,
  description,
  url,
  userId,
  centerId,
  iframeKey,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  url: string;
  userId?: string | number | null;
  centerId?: string | null;
  iframeKey: string;
}) => {
  const isSheetHost = useDoctorHomeSheetHost();
  const iframeSrc = buildDoctorPanelUrl(url, { userId, centerId });

  return (
    <DsDrawer
      level={1}
      open={open && isSheetHost}
      onOpenChange={o => {
        if (!o) onClose();
      }}
      title={title}
      description={description}
      fullHeight
      className="!p-0"
    >
      {open && (
        <iframe
          key={`${iframeKey}-${centerId ?? 'all'}`}
          src={iframeSrc}
          title={title}
          className="min-h-0 w-full flex-1 border-0"
        />
      )}
    </DsDrawer>
  );
};

const ActionSheetList = ({
  onSelect,
  actions,
}: {
  onSelect: (id: ActionId) => void;
  actions: typeof SUB_ACTIONS;
}) => (
  <ul className="pb-6">
    {actions.map((action, index) => {
      const Icon = action.icon;
      return (
        <li key={action.id}>
          <FeedStaggerItem index={index}>
          <button
            type="button"
            onClick={() => onSelect(action.id)}
            className={classNames(
              'flex w-full items-center gap-3 text-start',
              ds.motion.listRow,
              ds.layout.rowPaddingWide,
              index < actions.length - 1 && 'border-b border-slate-100',
            )}
          >
            <ActionIcon>
              <Icon size="sm" />
            </ActionIcon>
            <span className="min-w-0 flex-1">
              <p className={ds.type.cardTitle}>{action.label}</p>
              <p className={classNames(ds.type.caption, 'mt-0.5')}>{action.hint}</p>
            </span>
            <RowChevron />
          </button>
          </FeedStaggerItem>
        </li>
      );
    })}
  </ul>
);

const SETTINGS_SCENT = 'ساعات کاری، مرخصی، تعرفه';

/** ۴۰۹ لوتولی روی مسیر قدیمی = پزشکِ مشمول گارد با چت بازِ بی‌پاسخ */
const isHttpConflict = (err: unknown): boolean =>
  typeof err === 'object' && err !== null && (err as { response?: { status?: unknown } }).response?.status === 409;

/** آیکون‌های شیت گارد — عین OnlineVisitToggle نلسون‌فرانت */
const GuardCloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

function GuardWarningIcon() {
  return (
    <svg
      className="online-visit-guard-title-icon-svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

const CompactRow = ({
  icon,
  title,
  titleHint,
  subtitle,
  onPress,
  trailing,
  isLast,
  widgetShell = false,
}: {
  icon: ReactNode;
  title: string;
  titleHint?: string;
  subtitle?: string;
  onPress?: () => void;
  trailing?: ReactNode;
  isLast?: boolean;
  widgetShell?: boolean;
}) => (
  <div
    className={classNames(
      'flex items-center gap-2',
      widgetShell ? 'px-5 py-3.5' : ds.layout.rowPadding,
      !isLast && (widgetShell ? 'border-b border-slate-50' : 'border-b border-slate-100'),
    )}
  >
    <button
      type="button"
      onClick={onPress}
      className={classNames('flex min-w-0 flex-1 items-center gap-3 text-start', ds.motion.listRow)}
    >
      <ActionIcon size="lg">{icon}</ActionIcon>
      <span className="min-w-0 flex-1">
        <p className={classNames(ds.type.cardTitle, 'flex min-w-0 items-baseline gap-2')}>
          <span className="shrink-0">{title}</span>
          {titleHint ? (
            <span className="min-w-0 truncate text-[10px] font-normal text-slate-400">{titleHint}</span>
          ) : null}
        </p>
        {subtitle ? <p className={classNames(ds.type.caption, 'mt-0.5 line-clamp-1')}>{subtitle}</p> : null}
      </span>
    </button>
    {trailing}
    {onPress ? (
      <button type="button" onClick={onPress} tabIndex={-1} aria-hidden className={ds.motion.listRow}>
        <RowChevron />
      </button>
    ) : null}
  </div>
);

export const FeedActionsSection = ({
  widgetShell = false,
  hideSectionHeader = false,
  nested = false,
}: {
  widgetShell?: boolean;
  hideSectionHeader?: boolean;
  nested?: boolean;
}) => {
  const user = useUserInfoStore(state => state.info);
  const userId = user?.id;
  const onlineVisitCenter = getOnlineVisitCenter(user);
  const onlineVisit = {
    userCenterId: (onlineVisitCenter as { user_center_id?: string } | undefined)?.user_center_id,
    hasOnlineVisitCenter: hasOnlineVisitCenter(user),
  };
  const onlineSheet = useSheetRoute('online-visit-actions');
  const clinicSheet = useSheetRoute('clinic-actions');
  const workhoursSheet = useSheetRoute('workhours');
  const vacationSheet = useSheetRoute('vacation');
  const tariffSheet = useSheetRoute('tariff');
  const bookingSettingsSheet = useSheetRoute('bookingSettings');
  const notificationsSheet = useSheetRoute('notifications');
  const onlineDrawerProps = useSheetDrawerProps(onlineSheet);
  const clinicDrawerProps = useSheetDrawerProps(clinicSheet);

  const selectedCenterId = useSelectedCenterStore(state => state.selectedCenterId);
  const setSelectedCenterId = useSelectedCenterStore(state => state.setSelectedCenterId);
  const selectedDate = useSelectedDateStore(state => state.selectedDate);
  const selectedMoment = moment(selectedDate, 'YYYY-MM-DD');
  const isToday = selectedMoment.isSame(moment(), 'day');
  const sectionTitle = isToday
    ? 'برنامه امروز'
    : `برنامه ${selectedMoment.clone().locale('fa').format('jD jMMMM')}`;

  const clinics = useMemo(() => getType1ClinicCenters(user), [user]);
  const showOnlineVisit = shouldShowOnlineVisitSection(selectedCenterId);
  const showClinic = shouldShowClinicSection(selectedCenterId, user);
  const activeClinic = useMemo(() => {
    if (!showClinic || clinics.length === 0) return null;
    if (selectedCenterId) {
      return clinics.find(center => String(center.id) === selectedCenterId) ?? clinics[0];
    }
    return clinics[0];
  }, [clinics, selectedCenterId, showClinic]);

  const { data: canBooking, isLoading: isOnlineVisitQueryLoading } = useOnlineVisitStatus(onlineVisit?.userCenterId);
  const isOnlineVisitInitialLoading = isOnlineVisitQueryLoading && canBooking == null;
  const isActive = canBooking === 1;
  const setOnlineVisitStatus = useSetOnlineVisitStatus();

  // --- ov-booking-guard (پیایلوت: دکتر بیگی — همان سوییچ «مراجعین من» در نلسون‌فرانت) ---
  const ovGuardEnabled = useOvBookingGuardEnabled();
  const [guardBlocked, setGuardBlocked] = useState<{ openCount: number; chats: OnlineVisitGuardChat[] } | null>(null);
  const [guardPending, setGuardPending] = useState<'enable' | 'disable' | null>(null);
  const [futureBooks, setFutureBooks] = useState<number | null>(null);
  const [isCancellingBooks, setIsCancellingBooks] = useState(false);
  const guardBusy = guardPending !== null;

  const showToggleToast = (nextActive: boolean) =>
    toast.success(
      nextActive
        ? 'نوبت‌دهی ویزیت آنلاین در ساعت کاری تعریف شده فعال شد. لطفا در «راس زمان نوبت بیمار» پاسخگو باشید.'
        : 'ویزیت آنلاین شما «غیرفعال» شد. در زمانی که قادر به پاسخگویی به بیماران در راس زمان نوبت بیمار هستید وضعیت ویزیت آنلاین خود را «فعال» کنید.',
    );

  /**
   * فعال‌سازی از مسیر گارد لوتولی — مشترک بین شاخهٔ فلگ‌دار و بازگشت از ۴۰۹ مسیر قدیمی.
   * چت بازِ بی‌پاسخ ⇒ هیچ تغییری نکرده‌ایم و شیت جدول چت‌ها؛ وگرنه read-back سرور ملاک است.
   * خطای شبکه به فراخواننده پرتاب می‌شود.
   */
  const runGuardedEnable = async (userCenterId: string, source?: 'legacy_409') => {
    const res = await enableOnlineVisitGuarded(userCenterId);
    if (res.allowed === false) {
      const chats = res.chats ?? [];
      const openCount = Number(res.open_count ?? chats.length ?? 0);
      setGuardBlocked({ openCount, chats });
      sendDoctorHomeEvent(userId, 'online_visit_guard_blocked', {
        user_center_id: userCenterId,
        open_count: openCount,
        chats_count: chats.length,
        linked_count: chats.filter(chat => Boolean(chat.book_id)).length,
        ...(source ? { source } : {}),
      });
      sendDoctorHomeEvent(userId, 'online_visit_toggle', {
        is_on: false,
        blocked_by_guard: true,
        open_count: openCount,
      });
      return;
    }
    const next = Number(res.can_booking ?? 1);
    setOnlineVisitStatus(userCenterId, next);
    showToggleToast(next === 1);
    sendDoctorHomeEvent(userId, 'online_visit_toggle', { is_on: next === 1, guarded: true });
  };

  // هر دو مسیر عین نلسون‌فرانت: تا پاسخ سرویس نیامده وضعیت عوض نمی‌شود (فقط لودینگ)؛
  // read-back سرور ملاک است، نه optimistic؛ شکست = بدون تغییر + پیام خطا.
  const handleToggle = async (checked: boolean) => {
    if (!onlineVisit?.userCenterId || guardBusy || isOnlineVisitInitialLoading) return;
    const userCenterId = onlineVisit.userCenterId;

    if (!ovGuardEnabled) {
      setGuardPending(checked ? 'enable' : 'disable');
      try {
        let futureCount = 0;
        if (!checked) {
          try {
            futureCount = await getOnlineVisitCountAfterDeactivation(userCenterId);
          } catch {
            toast.error('دریافت نوبت‌های آینده انجام نشد — دوباره تلاش کنید');
            return;
          }
        }
        const next = await toggleCanBooking(userCenterId, isActive ? 1 : 0);
        setOnlineVisitStatus(userCenterId, next);
        showToggleToast(next === 1);
        sendDoctorHomeEvent(userId, 'online_visit_toggle', { is_on: next === 1 });
        if (!checked && next !== 1 && futureCount > 0) setFutureBooks(futureCount);
      } catch (err) {
        // ۴۰۹ هنگام فعال‌سازی = لوتولی این پزشک را مشمول قانون چت باز می‌داند (فلگ از فهرست
        // پویای سرور خبر ندارد). بدنهٔ ۴۰۹ مسیر قدیمی جدول چت‌ها را ندارد؛ همان مسیر گارد
        // پرسیده می‌شود تا پزشک شیت چت‌ها را ببیند، نه توست عمومی (تصمیم مالک ۲۰۲۶-۱۰-۲۵).
        if (checked && isHttpConflict(err)) {
          try {
            await runGuardedEnable(userCenterId, 'legacy_409');
          } catch {
            toast.error('تغییر وضعیت انجام نشد — دوباره تلاش کنید');
          }
          return;
        }
        toast.error('تغییر وضعیت انجام نشد — دوباره تلاش کنید');
      } finally {
        setGuardPending(null);
      }
      return;
    }

    // مسیر گارد (لوتولی)
    setGuardPending(checked ? 'enable' : 'disable');
    try {
      if (checked) {
        await runGuardedEnable(userCenterId);
      } else {
        const res = await disableOnlineVisitGuarded(userCenterId);
        const next = Number(res.can_booking ?? 0);
        setOnlineVisitStatus(userCenterId, next);
        showToggleToast(next === 1);
        sendDoctorHomeEvent(userId, 'online_visit_toggle', { is_on: false, guarded: true });
        const futureCount = Number(res.future_count ?? 0);
        if (futureCount > 0) setFutureBooks(futureCount);
      }
    } catch {
      toast.error('تغییر وضعیت انجام نشد — دوباره تلاش کنید');
    } finally {
      setGuardPending(null);
    }
  };

  const handleDismissGuardBlock = () => {
    if (guardBlocked) {
      sendDoctorHomeEvent(userId, 'online_visit_guard_sheet_close', {
        user_center_id: onlineVisit?.userCenterId,
        open_count: guardBlocked.openCount,
        chats_count: guardBlocked.chats.length,
      });
    }
    setGuardBlocked(null);
  };

  const handleGuardChatClick = (chat: OnlineVisitGuardChat) => {
    sendDoctorHomeEvent(userId, 'online_visit_guard_chat_click', {
      user_center_id: onlineVisit?.userCenterId,
      ticket_id: chat.ticket_id,
      hours_overdue: Math.round(Number(chat.hours_overdue ?? 0)),
      linked: Boolean(chat.book_id),
      open_count: guardBlocked?.openCount,
    });
  };

  const handleCancelFutureBooks = async () => {
    if (!onlineVisit?.userCenterId || isCancellingBooks) return;
    setIsCancellingBooks(true);
    try {
      const deleteBooks = ovGuardEnabled
        ? deleteBooksAfterDeactiveOnlineVisitGuarded
        : deleteBooksAfterDeactiveOnlineVisit;
      await deleteBooks(onlineVisit.userCenterId);
      toast.success('نوبت‌ها با موفقیت لغو شدند.');
      setFutureBooks(null);
      sendDoctorHomeEvent(userId, 'online_visit_cancel_books', { user_center_id: onlineVisit.userCenterId });
    } catch {
      toast.error('لغو نوبت‌ها انجام نشد');
    } finally {
      setIsCancellingBooks(false);
    }
  };

  const [panelCenterId, setPanelCenterId] = useState<string | null>(null);

  const sheets = {
    workhours: workhoursSheet,
    vacation: vacationSheet,
    tariff: tariffSheet,
    bookingSettings: bookingSettingsSheet,
    notifications: notificationsSheet,
  } as const;
  const actionIds = Object.keys(sheets) as ActionId[];

  const openAction = (id: ActionId, centerId?: string | null) => {
    const action = SUB_ACTIONS.find(item => item.id === id);
    if (!action) return;
    sendDoctorHomeEvent(userId, action.analyticsFeature);
    setPanelCenterId(centerId ?? selectedCenterId);
    // منوی والد در استک می‌ماند؛ بک / بستن iframe به منو برمی‌گردد
    sheets[id].openSheetOver(actionIds.filter(actionId => actionId !== id));
  };

  const resolvedPanelCenterId = panelCenterId ?? selectedCenterId;

  const openOnlineSheet = () => {
    sendDoctorHomeEvent(userId, 'online_visit_actions_open');
    onlineSheet.openSheet();
  };

  const openActivationPage = () => {
    sendDoctorHomeEvent(userId, 'online_visit_activation_open');
    window.open(
      buildDoctorPanelUrl(DOCTOR_PANEL_URLS.consultActivation, { userId }),
      '_blank',
      'noopener,noreferrer',
    );
  };

  const hasClinic = !!activeClinic;
  const showClinicRow = showClinic && hasClinic;
  const clinicTitle = activeClinic ? formatDoctorCenterName(activeClinic) : '';
  const clinicAddressHint = activeClinic ? formatDoctorCenterAddressHint(activeClinic) : undefined;
  const clinicSubtitle =
    clinics.length > 1 && !selectedCenterId
      ? `${clinics.length.toLocaleString('fa-IR')} مطب · ${SETTINGS_SCENT}`
      : SETTINGS_SCENT;

  const onlineSubtitle = !onlineVisit.hasOnlineVisitCenter
    ? 'فعال‌سازی نوبت آنلاین'
    : isOnlineVisitInitialLoading
      ? 'در حال بررسی…'
      : guardPending
        ? guardPending === 'enable'
          ? 'در حال فعال‌سازی…'
          : 'در حال غیرفعال‌سازی…'
        : SETTINGS_SCENT;

  const onlineActions = useMemo(
    () =>
      SUB_ACTIONS.map(action => {
        if (action.id === 'vacation') return { ...action, hint: 'مرخصی ویزیت آنلاین' };
        if (action.id === 'bookingSettings') return { ...action, hint: 'تنظیم محدودیت‌های گفت‌وگو' };
        return action;
      }),
    [],
  );
  const clinicActions = useMemo(
    () =>
      SUB_ACTIONS.filter(
        action => action.id !== 'bookingSettings' || isType1Center(activeClinic),
      ),
    [activeClinic],
  );

  if (!showOnlineVisit && !showClinicRow) return null;

  const rows = (
    <>
        {hideSectionHeader && !nested && (
          <div className="border-b border-slate-100 px-3 py-2.5">
            <p className={ds.type.section}>{sectionTitle}</p>
          </div>
        )}
        {showOnlineVisit && (
          <CompactRow
            icon={<ChatIcon size="md" />}
            title="ویزیت آنلاین"
            widgetShell={widgetShell}
            subtitle={onlineSubtitle}
            trailing={
              onlineVisit.hasOnlineVisitCenter ? (
                <Switch
                  checked={isActive}
                  onChange={e => handleToggle(e.target.checked)}
                  disabled={isOnlineVisitInitialLoading || guardBusy}
                  aria-label="فعال‌سازی نوبت‌دهی آنلاین"
                />
              ) : (
                <DsButton variant="primary" className="!h-8 !min-h-0 !px-2.5 !py-1" onClick={openActivationPage}>
                  شروع
                </DsButton>
              )
            }
            isLast={!showClinicRow && !nested}
            onPress={() => {
              if (!onlineVisit.hasOnlineVisitCenter) {
                openActivationPage();
                return;
              }
              openOnlineSheet();
            }}
          />
        )}

        {showClinicRow && (
          <CompactRow
            icon={<ClinicIcon size="md" />}
            title={clinicTitle}
            titleHint={clinicAddressHint}
            subtitle={clinicSubtitle}
            widgetShell={widgetShell}
            isLast={!nested}
            onPress={() => {
              sendDoctorHomeEvent(userId, 'clinic_actions_open');
              clinicSheet.openSheet();
            }}
          />
        )}
    </>
  );

  const drawers = (
    <>
      <DsDrawer
        {...onlineDrawerProps}
        title="ویزیت آنلاین"
        description="مدیریت نوبت آنلاین"
      >
        <ActionSheetList
          actions={onlineActions}
          onSelect={id => openAction(id, ONLINE_VISIT_CENTER_ID)}
        />
      </DsDrawer>

      <DsDrawer
        {...clinicDrawerProps}
        title={clinicTitle}
        description="مدیریت مطب"
      >
        {clinics.length > 1 && (
          <div role="group" aria-label="انتخاب مطب" className="mb-1 flex gap-2 overflow-x-auto px-3 pb-2 pt-1 no-scroll">
            {clinics.map(clinic => {
              const clinicId = String(clinic.id);
              const isSelected = String(activeClinic?.id) === clinicId;
              const name = formatDoctorCenterName(clinic);
              const addressHint = formatDoctorCenterAddressHint(clinic);

              return (
                <button
                  key={clinicId}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => {
                    setSelectedCenterId(clinicId);
                    sendDoctorHomeEvent(userId, 'clinic_picker_select', { center_id: clinicId });
                  }}
                  className={classNames(
                    'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1.5',
                    ds.motion.surface,
                    ds.motion.press,
                    ds.type.label,
                    dsFocusRing,
                    isSelected
                      ? classNames(ds.surface.muted, 'border-slate-300 text-slate-800')
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50',
                  )}
                >
                  <ClinicIcon size="xs" />
                  {name}
                  {addressHint ? (
                    <span className="text-[10px] font-normal text-slate-400">{addressHint}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
        <ActionSheetList
          actions={clinicActions}
          onSelect={id => openAction(id, activeClinic ? String(activeClinic.id) : null)}
        />
      </DsDrawer>

      {/* گارد لوتولی — شیت چت‌های بی‌پاسخ (فقط پزشکان فلگ‌خورده؛ read-back یعنی هیچ تغییری نکرده‌ایم).
          ظاهر عین شیت «مراجعین من» نلسون‌فرانت: handle + هدر با آیکون هشدار و × + ردیف‌های کامل‌لینک
          (مارک‌آپ و CSS پورت‌شده — تصمیم owner 2026-10-12). */}
      {guardBlocked !== null &&
        createPortal(
          <div className="ov-guard-sheet-root" dir="rtl">
            <button
              type="button"
              className="ov-guard-sheet-backdrop"
              aria-label="بستن"
              onClick={handleDismissGuardBlock}
            />
            <section
              className="ov-guard-sheet"
              role="dialog"
              aria-modal="true"
              aria-label="فعال‌سازی ویزیت آنلاین ممکن نیست"
            >
              <div className="ov-guard-sheet-handle" aria-hidden />
              <header className="ov-guard-sheet-header">
                <h3 className="ov-guard-sheet-title">
                  <span className="online-visit-guard-title-icon" aria-hidden="true">
                    <GuardWarningIcon />
                  </span>
                  فعال‌سازی ویزیت آنلاین ممکن نیست
                </h3>
                <button
                  type="button"
                  className="ov-guard-sheet-close"
                  aria-label="بستن"
                  onClick={handleDismissGuardBlock}
                >
                  <GuardCloseIcon />
                </button>
              </header>
              <div className="ov-guard-sheet-body">
                <OnlineVisitGuardChats
                  chats={guardBlocked.chats}
                  openCount={guardBlocked.openCount}
                  onChatClick={handleGuardChatClick}
                />
              </div>
            </section>
          </div>,
          document.body,
        )}

      <Modal
        isOpen={futureBooks !== null}
        onClose={() => setFutureBooks(null)}
        title="نوبت‌دهی ویزیت آنلاین شما غیرفعال شد"
      >
        <div dir="rtl" className="space-y-3">
          <p className="text-sm leading-6 text-slate-600">
            {futureBooks === 1
              ? '۱ نوبت ویزیت آنلاین برای شما ثبت شده که مربوط به ساعات آینده است و هنوز ویزیت نشده‌است.'
              : `${(futureBooks ?? 0).toLocaleString('fa-IR')} نوبت ویزیت آنلاین برای شما ثبت شده که مربوط به ساعات آینده است و هنوز ویزیت نشده‌اند.`}
          </p>
          <p className={ds.type.cardTitle}>آیا مایل به لغو این نوبت‌ها هستید؟</p>
          <p className={ds.type.caption}>در صورت لغو نوبت برای بیمار(ان) پیامک ارسال خواهد شد.</p>
          <div className="flex flex-col gap-2 pt-1 sm:flex-row">
            <DsButton variant="primary" className="!min-h-[2.5rem]" onClick={handleCancelFutureBooks}>
              لغو نوبت
            </DsButton>
            <DsButton variant="secondary" className="!min-h-[2.5rem]" onClick={() => setFutureBooks(null)}>
              {futureBooks === 1 ? 'نوبت را ویزیت خواهم کرد' : 'نوبت‌ها را ویزیت خواهم کرد'}
            </DsButton>
          </div>
        </div>
      </Modal>

      {SUB_ACTIONS.map(action => (
        <PanelIframeDrawer
          key={action.id}
          open={sheets[action.id].open}
          onClose={() => {
            sheets[action.id].closeSheet();
            setPanelCenterId(null);
          }}
          title={action.label}
          description={action.description}
          url={action.url}
          userId={userId}
          centerId={resolvedPanelCenterId}
          iframeKey={action.id}
        />
      ))}
    </>
  );

  if (nested) {
    return (
      <>
        {rows}
        {drawers}
      </>
    );
  }

  return (
    <section dir="rtl">
      {!hideSectionHeader && <DsSectionHeader title={sectionTitle} />}
      <DsCard padding="none" variant={widgetShell ? 'widget' : 'default'} className="overflow-hidden">
        {rows}
      </DsCard>
      {drawers}
    </section>
  );
};
